import { slugify } from "../../../../scripts/changelog/sections.mjs";
import { getPostSlugFromPath, type PostFrontmatter } from "./blogLabels";

// Globbed beside the routes for the reason changelog/entries.ts gives: picomatch reads
// the `(post)` directory as a regex group.
const metaModules = import.meta.glob<{ frontmatter: PostFrontmatter }>("./**/*.mdx", { eager: true });

const sourceModules = import.meta.glob<string>("./**/*.mdx", {
  eager: true,
  query: "?raw",
  import: "default",
});

export type TocItem = { depth: 2 | 3; text: string; slug: string };

export type Post = PostFrontmatter & { slug: string; readingTime: number; toc: TocItem[] };

// A draft merges hidden: it renders on preview deployments, so a draft PR can carry it,
// and nowhere in production. Locally VERCEL_ENV is empty, so drafts show.
const isVisible = (fm: PostFrontmatter) => !fm.draft || VERCEL_ENV !== "production";

const FRONTMATTER = /^---\r?\n[\s\S]*?\r?\n---\r?\n/;
const FENCE = /^\s{0,3}(?:`{3,}|~{3,})/;
const HEADING = /^(#{2,3})\s+(.+?)\s*$/;
const WORDS_PER_MINUTE = 230;

// The on-this-page list and the reading time, from the source at build time so the writer
// never types either. Anchor ids come from the same slugify the headings render with.
// ponytail: a heading with inline markup renders no id (SectionHeading slugs plain text
// only), so its TOC link goes nowhere; parse the MDX tree if posts start formatting headings.
function outlineOf(source: string): Pick<Post, "readingTime" | "toc"> {
  const body = source.replace(FRONTMATTER, "");
  const toc: TocItem[] = [];
  let fenced = false;
  for (const line of body.split(/\r\n|\r|\n/)) {
    if (FENCE.test(line)) fenced = !fenced;
    const heading = fenced ? null : HEADING.exec(line);
    if (heading) toc.push({ depth: heading[1].length as 2 | 3, text: heading[2], slug: slugify(heading[2]) });
  }
  const words = body
    .replace(/^import .*$/gm, "")
    .replace(/<[^>]*>/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  return { toc, readingTime: Math.max(1, Math.round(words / WORDS_PER_MINUTE)) };
}

/** Every post the current deployment shows, newest first. */
export const visiblePosts: Post[] = Object.entries(metaModules)
  .filter(([, mod]) => isVisible(mod.frontmatter))
  .map(([path, mod]) => ({
    ...mod.frontmatter,
    slug: getPostSlugFromPath(path),
    ...outlineOf(sourceModules[path] ?? ""),
  }))
  .toSorted((a, b) => (a.date < b.date ? 1 : -1));

export const postBySlug = new Map(visiblePosts.map((post) => [post.slug, post]));
